<template>
  <div id="editorContent">
    <EditorHeader
      :unsaved-changes="$store.state.unsavedChanges"
      @comparison-view="comparisonView = !comparisonView"
      class="editor-header"
    />
    <div v-if="comparisonView" class="multi-preview wrapper custom-scrollbar-light" :class="desiredLayoutColumns">
      <div
        v-for="(previewItem, index) in templateMultiplePreview"
        :key="index"
        class="single-item"
        @click="setAsMain(previewItem, index)"
      >
        <!-- <div class="single-item-header d-flex flex-grow-1">	 -->
        <div class="single-item-header">
          <div class="toolbar-sections d-flex flex-grow-1">
            <div
              class="toolbar-section"
              @click="changeSingleItemData(previewItem, index)"
              :tooltip="$store.state.showTooltips == true ? $t('toolbar.change-data') : ''"
              tooltip-placement="bottom"
            >
              <div class="menu-toggle" :class="{ 'search-anim': showTemplateDataPopover }">
                <i class="fa-light fa-search"></i>
              </div>
            </div>
            <div
              class="toolbar-section"
              @click="randomizeSingleItem(previewItem)"
              :tooltip="$store.state.showTooltips == true ? $t('toolbar.randomize') : ''"
              tooltip-placement="bottom"
              :style="previewItem && previewItem.error == true ? 'opacity:0.5;pointer-events:none;' : ''"
            >
              <div class="menu-toggle" :class="{ 'turn-anim': randomized == previewItem }">
                <div v-show="!randomized || randomized != previewItem"><i class="fa-light fa-random"></i></div>
                <div v-show="randomized == previewItem"><i class="fa-light fa-circle-notch fa-spin"></i></div>
              </div>
            </div>
            <div
              class="toolbar-section"
              @click="showSingleItemStats(previewItem, index)"
              :tooltip="$store.state.showTooltips == true ? $t('toolbar.stats') : ''"
              tooltip-placement="bottom"
              :style="previewItem && previewItem.error == true ? 'opacity:0.5;pointer-events:none;' : ''"
            >
              <div class="menu-toggle" :class="{ 'search-anim': showStatsPopover }">
                <i class="fa-light fa-chart-pie"></i>
              </div>
            </div>
            <img
              @click.stop="removeSingleItem(previewItem, index)"
              class="close-popover"
              src="@/assets/svg/close.svg"
            />
          </div>

          <div
            class="reference-name"
            :style="$store.state.selectedDataset == previewItem.selectedDataset ? 'color:#3D464A;' : ''"
            :class="{ disable: previewItem && previewItem.error == true }"
          >
            <div class="loading-spinner" v-show="previewItem.loading == true" style="margin-right: 4px;">
              <div class="fa-light fa-circle-notch fa-spin"></div>
            </div>
            <span>{{ previewItem.selectedDataset ? previewItem.selectedDataset.referenceName : "Example data" }}</span>
          </div>
        </div>

        <div class="section-wrapper custom-scrollbar-light" :class="desiredLayoutRows">
          <div v-if="previewItem && previewItem.hideText" :style="previewItem.loading == true ? 'height: 105px;' : ''">
            <div
              class="error text-danger d-flex justify-content-center align-items-center flex-column"
              v-if="previewItem && previewItem.error == true"
            >
              <span style="margin-bottom:12px;"> {{ $t("multiview.data-load-error") }}</span>
              <button style="width:160px;" class="btn btn-secondary" @click="changeSingleItemData(previewItem, index)">
                <div class="menu-toggle" :class="{ 'search-anim': showTemplateDataPopover }">
                  <i class="fa-light fa-search"></i> {{ $t("multiview.data-load-error-btn") }}
                </div>
              </button>
            </div>
          </div>
          <div v-else>
            <EditorSection
              :key="section.id"
              :preview-section="(previewItem.preview.sections.find((s) => s.id === section.id) || {}).result || {}"
              :relevantVariables="previewItem.variables"
              :section-id="section.id"
              :section-type="section.type"
              :section-name="section.name"
              :template-section="section"
              :multiPreview="true"
              :showRawHTML="showRawHTML"
              v-for="(section, index) in templateSections"
              @remove="onRemoveSection(index)"
              @create-above="onAddSection(index)"
              @create-below="onAddSection(index + 1)"
              @initialize="onInitialize(section.id, previewItem)"
              @cleaned-section="(cleaner) => combineSections(cleaner, previewItem)"
            />
            <div class="dash-comparison-view "></div>
          </div>
        </div>
      </div>
      <div class="add-preview">
        <button class="btn" @click="addPreviewToMultiplePreviews">+ {{ $t("multiview.add-preview") }}</button>
      </div>
    </div>
    <div v-else class="wrapper custom-scrollbar-light">
      <EditorSection
        :key="section.id"
        :preview-section="($store.state.templatePreview.sections.find((s) => s.id === section.id) || {}).result || {}"
        :section-id="section.id"
        :section-name="section.name"
        :template-section="section"
        :section-type="section.type"
        :showRawHTML="showRawHTML"
        v-for="(section, index) in templateSections"
        @remove="onRemoveSection(index)"
        @create-above="onAddSection(index)"
        @create-below="onAddSection(index + 1)"
        @clone-below="
          (id) => {
            onClone(id, index + 1, section.name);
          }
        "
        @initialize="onInitialize(section.id)"
        @cleaned-section="combineSections"
      />
      <div class="add-section" v-if="!$store.state.showingTemplateBackupInPreview">
        <a
          @click="
            handleAction($event, 'default', () => {
              onAddSection();
            })
          "
          >{{ $t("editor-content.add-section-btn") }}
        </a>
      </div>
    </div>
    <div v-if="$store.state.sentenceBeingEdited">
      <AiSectionPopover
        v-if="sectionBeingEdited.type && sectionBeingEdited.type == 'ai'"
        :template-section="sectionBeingEdited"
      />

      <SentenceEditPopover
        v-else
        :template="$store.state.template"
        :sentence="$store.state.sentenceBeingEdited"
        :sentence-element="sentenceElement"
        :template-section="sectionBeingEdited"
        :comparisonView="comparisonView"
      />
    </div>
    <div :class="comparisonView ? '' : 'dash'"></div>
    <TemplateDataPopover
      v-if="showTemplateDataPopover"
      @close="showTemplateDataPopover = false"
      :multiPreview="true"
      :singleItemToReplace="singleItemToReplace"
    />
    <TemplateStatsPopover
      v-if="showStatsPopover"
      @close="(showStatsPopover = false), (previewForStats = null)"
      :allText="previewForStats"
      :selectedDataset="selectedDataset"
    />
  </div>
</template>

<script>
import { editorMixin } from "@/assets/js/editorMixin.js";
import TemplateStatsPopover from "@/components/TemplateStatsPopover";
import TemplateDataPopover from "@/components/TemplateDataPopover";
import EditorSection from "@/components/EditorSection";
import SentenceEditPopover from "@/components/SentenceEditPopover";
import AiSectionPopover from "@/components/AiSectionPopover";
import EditorHeader from "@/components/EditorHeader";
import RenameModal from "@/components/modals/RenameModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import EventBus from "@/assets/js/eventBus.js";
import addSectionForm from "@/assets/js/AddSectionForm.js";
import router from "@/router";
import DynamicFormModal from "@/components/modals/DynamicFormModal.vue";

// const addSectionForm = require("@/assets/js/AddSectionForm.js");
export default {
  mixins: [editorMixin],
  data() {
    return {
      template: this.$store.state.template,
      // sectionId: null,
      sentenceElement: null,
      sectionBeingEdited: null,
      combinedSections: [],
      searchQuery: "Specielt",
      comparisonView: false,
      // datasetsOpen:[]
      showTemplateDataPopover: false,
      showStatsPopover: false,
      desiredLayoutColumns: "column-two",
      desiredLayoutRows: "row-one",
      randomized: false,
      singleItemToReplace: null,
      previewForStats: null,
      selectedDataset: this.$store.state.selectedDataset,
      showRawHTML: false,
      validNewName: false,
      AiSectionAllowed: false
    };
  },
  components: {
    EditorSection,
    SentenceEditPopover,
    EditorHeader,
    TemplateDataPopover,
    TemplateStatsPopover,
    AiSectionPopover
  },
  computed: {
    templateSections() {
      return this.$store.state.template.sections;
    },
    templateMultiplePreview() {
      return this.$store.state.templateMultiplePreview;
    },
    mulitPreviewItems() {
      return this.$store.state.templateMultipleDatasets;
    },
    variables() {
      return this.$store.state.templateVariables;
    },
    isMain() {
      return this.$store.state.selectedDataset;
    },
    previewAsHTML() {
      return this.$store.state.templatePreviewAsHTML;
    }
  },
  methods: {
    showSingleItemStats(previewItem, index) {
      this.selectedDataset = previewItem.selectedDataset;
      this.$store.state.templateVariables = previewItem.variables;
      this.singleItemToReplace = index;
      this.previewForStats = previewItem.statsData;
      this.showStatsPopover = true;
    },
    changeSingleItemData(previewItem, index) {
      this.singleItemToReplace = index;
      this.showTemplateDataPopover = true;
    },
    removeSingleItem(previewItem, index) {
      this.$store.state.templateMultiplePreview.splice(index, 1);
    },
    randomizeSingleItem(previewItem) {
      this.randomized = previewItem;
      router.currentRoute.query.seed = Math.floor(Math.random() * 10000) + 1;
      this.$store
        .dispatch("getTemplatePreview", {
          template: this.$store.state.template,
          variables: previewItem.variables,
          generateAIContent: true
        })
        .then((newPreview) => {
          this.randomized = false;
          previewItem.preview = newPreview;
        });
    },
    setAsMain(previewItem, index) {
      if (!previewItem.error) {
        this.$store.state.templateVariables = previewItem.variables;
        this.$store.state.selectedDataset = previewItem.selectedDataset;
        // this.$store.state.templatePreview = previewItem.preview;
        this.$store.commit("setTemplatePreview", previewItem.preview);
      }
    },
    addPreviewToMultiplePreviews() {
      this.singleItemToReplace = null;
      this.showTemplateDataPopover = true;
    },
    combineSections(cleaner, previewItem) {
      if (previewItem) {
        if (
          previewItem.statsData &&
          previewItem.statsData.find((sections) => sections.sectionId === cleaner.sectionId)
        ) {
          let currentIndex = previewItem.statsData.findIndex((sections) => sections.sectionId === cleaner.sectionId);
          previewItem.statsData[currentIndex].sectionText = cleaner.sectionText;
        } else previewItem.statsData ? previewItem.statsData.push(cleaner) : (previewItem.statsData = [cleaner]);
      }
      if (this.combinedSections.find((sections) => sections.sectionId === cleaner.sectionId)) {
        let currentIndex = this.combinedSections.findIndex((sections) => sections.sectionId === cleaner.sectionId);
        this.combinedSections[currentIndex].sectionText = cleaner.sectionText;
      } else {
        this.combinedSections.push(cleaner);
      }
      EventBus.$emit("text_stats", this.combinedSections);
    },
    onInitialize(sectionId) {
      // empty section, let's initialize it
      EventBus.$emit("editSentence", { sentence: { path: [0, 0] }, element: null, sectionId: sectionId });
      // EventBus.$emit('nameSection', { sentence: { path: [0, 0] }, element: null, sectionId: sectionId })
    },
    onRemoveSection(index) {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t("editor-content.remove-section"),
          description: this.$t("editor-content.remove-section-desc"),
          confirmButtonText: this.$t("editor-content.remove-section-btn"),
          onConfirm: (result) => {
            this.$store.state.template.sections.splice(index, 1);
            this.$store.state.unsavedChanges = true;
          }
        },
        { width: 350, height: "auto", adaptive: true }
      );
    },
    getInvalidSectionNames() {
      return this.$store.state.template.sections.reduce(
        (all, current) => all.concat(Object.values({ name: current.name, id: current.id })),
        []
      );
    },

    onAddSection(index) {
      // Only get the ai section option if its enabled on the organization  
      const sectionForm = this.AiSectionAllowed ? addSectionForm : [addSectionForm[1]]
      this.$modal.show(
        DynamicFormModal,
        {
          title: this.$t("editor-content.add-section-title"),
          formData: sectionForm,
          primaryButton: this.$t("add"),
          source: {
            sectionName: "",
            sectionType: "text"
          },
          ignoreInvalid: ["sectionName"],          
          onChange: (source, callback) => {
            // Check new name and id
            const invalidInputs = this.getInvalidSectionNames();
            const nameAndId = [
              source.sectionName,
              source.sectionName
                .toLowerCase()
                .replace(/[^\w ]+/g, "")
                .replace(/ +/g, "")
            ];
            callback(!invalidInputs.some((e) => nameAndId.includes(e)));
          },
          aiDangerText: this.$t("ai-sections.danger-text"),
          onSubmit: (source) => {
            if (source.sectionName) {
              let id = source.sectionName
                .toLowerCase()
                .replace(/[^\w ]+/g, "")
                .replace(/ +/g, "");
              this.$store.state.template.addSection(
                id,
                source.sectionName,
                index,
                source.paragraph,
                source.sectionType
              );
              this.$store.state.unsavedChanges = true;
            }
          }
        },
        { width: 350, height: "auto" }
      );
    },

    onClone(id, index, sectionName) {
      this.$modal.show(
        RenameModal,
        {
          title: this.$t("editor-content.duplicate-section"),
          inputName: this.$t("editor-content.add-section-input"),
          inputPlaceholder: this.$t("editor-content.add-section-ph"),
          existingName: sectionName,
          primaryButton: this.$t("add"),
          invalidOptions: this.getInvalidSectionNames() || [],
          onSave: (name) => {
            const newId = name
              .toLowerCase()
              .replace(/[^\w ]+/g, "")
              .replace(/ +/g, "");
            this.template.duplicateSection(id, newId, name, index);
            this.$store.state.unsavedChanges = true;
            this.getTemplatePreview();
          }
        },
        { width: 350, height: "auto" }
      );
    },

    handleError(index) {
      this.$set(this.$store.state.templateMultiplePreview[index + 1], "error", true);
      this.$set(this.$store.state.templateMultiplePreview[index + 1], "loading", false);
    },

    toggleHTML() {
      if (this.comparisonView) {
        this.$store.dispatch("refreshAllPreviews");
      } else {
        this.getTemplatePreview();
      }
    },

    getTemplatePreview() {
      this.$store
        .dispatch("getTemplatePreview", {
          template: this.$store.state.template,
          variables: this.$store.state.templateVariables
        })
        .then((preview) => {
          this.$store.commit("setTemplatePreview", preview);
        });
    }
  },
  watch: {
    previewAsHTML: function() {
      this.toggleHTML();
    },
    comparisonView: function(newVal, oldVal) {
      if (this.comparisonView == true) {
        if (this.$store.state.templateMultiplePreview.length == 0) {
          //setting the main preview as the first to compare"
          // console.log("setting the main preview as the first to compare")
          let firstFavorites = this.$store.state.dataFavorites.slice(0, 3);
          firstFavorites.forEach((favorite, index) => {
            // console.log(index)
            let mulitPreviewItem = {
              selectedDataset: favorite,
              loading: true,
              hideText: true
            };
            this.$store.state.templateMultiplePreview.push(mulitPreviewItem);
            this.$store
              .dispatch("getVariables", {
                template: this.$store.state.template.metadata,
                id: favorite ? favorite.id : null
              })
              .then((vars) => {
                this.$store
                  .dispatch("getTemplatePreview", {
                    template: this.$store.state.template,
                    variables: vars,
                    generateAIContent: true
                  })
                  .then((preview) => {
                    const mulitPreviewItem = {
                      variables: vars,
                      preview: preview,
                      selectedDataset: favorite,
                      loading: false,
                      hideText: null
                    };
                    this.$store.state.templateMultiplePreview.splice(index + 1, 1, mulitPreviewItem);
                  })
                  .catch((e) => {
                    console.log(e);
                    this.handleError(index);
                  });
              })
              .catch((e) => {
                console.log(e);
                this.handleError(index);
              });
          });
          this.$nextTick(() => {
            const mainPreviewObject = {
              variables: this.$store.state.templateVariables,
              preview: this.$store.state.templatePreview,
              selectedDataset: this.$store.state.selectedDataset,
              loading: false
            };
            // this.$store.state.templateMultiplePreview.push(mainPreviewObject)
            this.$store.state.templateMultiplePreview.splice(0, 0, mainPreviewObject);
          });
        }
        EventBus.$emit("comparison_view_on");
        // EventBus.$emit('close_panel')
      } else {
        EventBus.$emit("comparison_view_off");
        EventBus.$emit("open_panel");
      }
    },
    selectedDataset: function(val, oldVal) {
      // console.log(this.selectedDataset)
      // console.log(this.$store.state.selectedDataset)
      this.$store.state.selectedDataset = this.selectedDataset;
    }
  },

  mounted(){
    //ensure organization object is present so we know if a user should be allowed to use AI
    this.$store.dispatch("getOrganization").then((res) => {
      this.AiSectionAllowed = res.aiEnabled
    })
  },
  
  created() {
    EventBus.$on("editSentence", ({ sentence, element, sectionId, variables }) => { 
      // this.sectionId = sectionId
      if (variables) {
        this.$store.state.templateVariables = variables;
      }
      this.sectionBeingEdited = this.$store.state.template.getSection(sectionId);
      this.sentenceElement = element;
      this.$store.commit("setSentenceBeingEdited", sentence);
    }),
      EventBus.$on("nameSection", (sectionName, sectionId) => {
        this.$store.commit("setTemplateSectionBeingNamed", sectionName);
        console.log(sectionName);
      });
    EventBus.$on("nameSectionID", (sectionId) => {
      this.$store.commit("setTemplateSectionBeingNamedAndID", sectionId);
      // this.$store.dispatch("getTemplateData", this.$route.params.templateId)
      console.log(sectionId);
      EventBus.$emit("sectionIDchanged");
    }),
      EventBus.$on("desired-layout-changed-columns", (desiredLayout) => {
        console.log(desiredLayout);
        this.desiredLayoutColumns = desiredLayout;
      }),
      EventBus.$on("desired-layout-changed-rows", (desiredLayout) => {
        console.log(desiredLayout);
        this.desiredLayoutRows = desiredLayout;
      }),
      EventBus.$on("new-preview", this.addPreviewToMultiplePreviews);
    this.$store.state.templateMultiplePreview = [];
    this.$store.state.templateMultipleDatasets = [];
  },
  destroyed() {
    EventBus.$off("desired-layout-changed-rows");
    EventBus.$off("desired-layout-changed-columns");
  }
};
</script>

<style lang="less">
#editorContent {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;

  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: "Roboto", sans-serif;
    text-transform: unset;
  }

  .wrapper {
    overflow-y: auto;
    height: calc(100vh - 55px - 40px);
  }

  .editor-section {
    .format-label-icon {
      z-index: 2;
    }
    &:first-child {
      .section-name {
        padding-top: 40px;
        .format-label-icon {
          top: 39px;
        }
      }
      .section-name-multi {
        padding-top: 40px;
        .format-label-icon {
          top: 39px;
        }
      }
      .section-text {
        padding-top: 38px;
      }
      .actions {
        padding-top: 24px;
      }
    }
  }

  .add-section {
    flex: 1 1 auto;
    padding-left: 15px;
    padding-top: 15px;
    padding-bottom: 15px;

    .btn {
      font-size: 14px;
    }
    // border-left: 1px dashed #d8d8d8;
    a {
      margin-left: 85px;
      cursor: pointer;
    }
  }
  .add-preview {
    flex: 1 1 auto;
    padding-left: 15px;
    padding-top: 15px;
    padding-bottom: 15px;
    button {
      font-size: 14px;
      color: var(--orange-web);
    }
  }
  // #EditorSection {
  //     flex: 1 1 auto;
  // }

  .dash {
    position: absolute;
    height: calc(100vh - 55px);
    max-height: 100vh;
    border-left: 1px dashed #d8d8d8;
    left: 85px;
    z-index: -1;
  }

  .multi-preview {
    display: grid;
    padding: 18px;
    grid-gap: 18px;
    .single-item {
      position: relative;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);

      .single-item-header {
        display: grid;
        // grid-template-columns: 1fr 2fr;
        grid-template-columns: auto 1fr;
        grid-template-rows: 1fr;

        position: relative;
        z-index: 99;
        // padding: 12px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);

        .toolbar-section {
          cursor: pointer;
          padding: 12px 24px;
          // border-right: 1px solid #D8D8D8;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .reference-name {
          grid-column-start: 2;
          justify-self: flex-end;
          align-self: center;
          position: relative;
          cursor: pointer;
          text-transform: uppercase;
          color: #8f979c;
          letter-spacing: 0.6px;
          padding: 0 20px;
          flex-basis: 50%;
          max-height: 40px;
          min-width: 0;

          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          max-width: 100%;

          .loading-spinner {
            position: absolute;
            left: 0px;
          }
        }
      }
      .dash-comparison-view {
        // flex:	1 1 auto ;
        position: absolute;
        top: 45px;
        z-index: 1;
        height: calc(100% - 45px);
        margin-left: 10px;
        padding-left: 10px;
        padding-top: 15px;
        border-left: 1px dashed #d8d8d8;
      }
    }

    .search-anim > svg.fa-search,
    .search-anim > svg.fa-chart-pie,
    .search-anim > svg.fa-chart-bar,
    .search-anim > svg.fa-book,
    .search-anim > svg.fa-atlas {
      animation: search 0.4s ease-out;
      transform-origin: 37% 50%;
    }
    .turn-anim > svg.fa-random,
    .turn-anim > svg.fa-sync-alt {
      animation: flash 0.4s infinite linear;
      transform-origin: 50% 50%;
    }
    .turn-anim {
      transition: 0.3s;
    }
    @keyframes search {
      0% {
        opacity: 1;
        transform: scale(1);
      }

      50% {
        opacity: 0.5;
        transform: scale(1.2);
      }

      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
    @keyframes flash {
      from {
        opacity: 0.8;
        transform: rotate(0deg) scale(0.8);
      }
      to {
        opacity: 0.8;
        transform: rotate(-360deg) scale(0.8);
      }
    }
    .section-wrapper {
      overflow-y: auto;
      overflow-x: hidden;
      // max-height: calc(40vh - 55px - 37px);
      // max-height: calc(35vh - 55px - 37px - 38px);
      .error {
        margin: 20px;
      }
    }

    .blur {
      -webkit-filter: blur(4px);
      filter: blur(4px);
    }
    img.close-popover {
      position: absolute;
      top: -9px;
      right: -8px;
      z-index: 101;
      cursor: pointer;
    }
  }
  .disable {
    pointer-events: none;
  }
  .column-one {
    grid-template-columns: repeat(1, 1fr);
  }
  .column-two {
    grid-template-columns: repeat(2, 1fr);
  }
  .column-three {
    grid-template-columns: repeat(3, 1fr);
  }
  // .row-one{max-height: calc(100vh - 55px - 37px - 38px);}
  // .row-two{max-height: calc(50vh - 55px - 37px - 24px - 45px);}
  // .row-three{ max-height: calc(35vh - 55px - 37px - 54px);}
  .row-one {
    max-height: calc(100vh - 55px - 37px - 80px);
  }
  .row-two {
    max-height: calc(50vh - 55px - 37px - 24px);
  }
  .row-three {
    max-height: calc(35vh - 55px - 37px - 24px);
  }
}
</style>
